import {URLS} from "./app-urls";
import {AbstractService, Expands, Filters} from "./abstract-service";
import {FtBom, GroupedFtBom, MountJiga, ReuseFtbMaterial, SavePartNumber} from "../models/basic/ft-bom";
import {AssociateProject, PartNumber} from "../models/basic/project";
import {AxiosResponse} from "axios";
import {trackPromise} from "react-promise-tracker";
import Api from "./api-config";
import {Paginated, PaginatedResult} from "../models/default/paginated-result";

export type FtBomFilter = Filters & {
  search?: string,
  associated?: string,
  ft_bom?: string,
  client?: string
};

export type FtBomPartNumberFilter = Filters & {
  client_id?: string,
  ft_bom?: string,
};


export class FtBomService extends AbstractService<FtBom> {
	constructor() {
		super(URLS.REPORT_FT_BOM);
	}
  
  override processFilters = (filters?: Filters) : Filters | undefined => {
    const processedFilter = {
      ...filters
    };
    
    if (processedFilter?.associated === "true" || processedFilter?.client === "0") {
      delete processedFilter.client;
    }
    
    return processedFilter;
  }
  
  async listFtBomsSearch(): Promise<AxiosResponse<{ ftboms: string[] }>> {
    const path = `${this.path}list_ftboms_search/`;
    return await trackPromise(Api.get(path));
  }
  
  async readPaginatedFtBomsGrouped(filters?: Filters, expands?: Expands, pagination?: Paginated): Promise<AxiosResponse<PaginatedResult<GroupedFtBom>>> {
    let path = `${this.path}grouped`;
    const allParams = this.processParameters(filters, expands, pagination);
    return await trackPromise(Api.get(path, {params: allParams} ));
  }

  async listFtBomsChildren(filters?: {client_id: number, ft_bom: string}): Promise<AxiosResponse<any>> {
    let path = `${this.path}list_ftboms_children/`;
    const allParams = this.processParameters(filters);
    return await trackPromise(Api.get(path, {params: allParams} ));
  }

  async changeStatus(payload: {client_id: number, status: boolean, ft_bom: string}): Promise<AxiosResponse<FtBom>> {
    const path = `${this.path}change_status/`;
    return await trackPromise(Api.post(path, payload));
  }

  async updateClient(payload: {client_id: number, ft_bom: string}): Promise<AxiosResponse<FtBom>> {
	const path = `${this.path}update_client/`;
	return await trackPromise(Api.post(path, payload));
  }

  async list_projects(client_id: string): Promise<AxiosResponse<AssociateProject[]>> {
    const path = `${this.path}list_projects/`;
    return await trackPromise(Api.get(path, { params: { client_id } }));
  }

  async list_partnumbers(client_id: string, project_id: string, ft_bom: string): Promise<AxiosResponse<PartNumber[]>> {
    const path = `${this.path}list_partnumbers/`;
    return await trackPromise(Api.get(path, { params: { client_id, project_id, ft_bom } }));
  }

  async save_part_numbers(payload: SavePartNumber): Promise<AxiosResponse<void>> {
    const path = `${this.path}save_part_numbers/`;
    return await trackPromise(Api.post(path, payload));
  }

  async listMaterials(filters?: Filters, expands?: Expands, pagination?: Paginated):  Promise<AxiosResponse<PaginatedResult<FtBom>>> {
    const path = `${this.path}list_materials/`;
    const allParams = this.processParameters(filters, expands, pagination);
    return await trackPromise(Api.get(path, {params: allParams} ));
  }

  async get_partnumbers_from_ftbom(filters?: FtBomPartNumberFilter):  Promise<AxiosResponse<SavePartNumber>> {
    const path = `${this.path}get_partnumbers_from_ftbom/`;
    return await trackPromise(Api.get(path, {params: filters} ));
  }

  async get_mount_jiga_groups(client_id: string, ft_bom: string, groups_list?: string[], search?: string): Promise<AxiosResponse<any>> {
	const path = `${this.path}assembly/list_jiga_groups/`;
	const groups = groups_list ? groups_list.join(',') : undefined;
	return await trackPromise(Api.get(path, {params: {client_id, ft_bom, groups, search}}));
  }

  async get_mount_jiga_group_children(filters?: {client_id: number, ft_bom: string, alternative_group_bom: string}): Promise<AxiosResponse<any>> {
	let path = `${this.path}assembly/list_jiga_group_children/`;
	const allParams = this.processParameters(filters);
	return await trackPromise(Api.get(path, {params: allParams} ));
  }

  async get_mount(client_id: string, ft_bom: string, type: string, groups_list?: string[], search?: string): Promise<AxiosResponse<any>> {
    const path = `${this.path}assembly/list_ftboms_items_assembly/`;
    const groups = groups_list ? groups_list.join(',') : undefined;
    return await trackPromise(Api.get(path, {params: {client_id, ft_bom, groups, search, type}}));
  }

  async save_mount(mount: any[], tipo: string): Promise<AxiosResponse<any>> {
    const path = `${this.path}assembly/mark_ftboms_items_assembly/`;
    return await trackPromise(Api.post(path, {ft_boms: mount}, {params: {tipo}}));
  }

  async get_status_mount(client_id: string, ft_bom: string, groups_list?: string[]): Promise<AxiosResponse<any>> {
    const path = `${this.path}assembly/get_status_mount/`;
    return await trackPromise(Api.get(path, {params: {client_id, ft_bom}}));
  }

  async load_ftboms_to_reuse(id: number): Promise<AxiosResponse<ReuseFtbMaterial[]>> {
    const path = `${this.path}reuse/${id}/list_ftboms/`;
    return await trackPromise(Api.get(path));
  }

  async load_reused_ftboms(client_id: string, ft_bom: string, alternative_group_bom: string): Promise<AxiosResponse<ReuseFtbMaterial[]>> {
    const path = `${this.path}reuse/list_ftboms_reused/`;
    return await trackPromise(Api.get(path, {params: {client_id, ft_bom, alternative_group_bom}}));
  }

  async save_reuse(id: number, ft_boms: ReuseFtbMaterial[]): Promise<AxiosResponse<any>> {
    const path = `${this.path}reuse/${id}/save_reuse/`;
    return await trackPromise(Api.post(path, {ft_boms: ft_boms}));
  }

  async get_last_updated_sap(): Promise<AxiosResponse<{ dt_ultima_atualizacao: string }>> {
    const path = `${this.path}get_last_updated_sap/`;
    return await trackPromise(Api.get(path));
  }
}
